import { useEffect, useState } from 'react';
import { parseMoney, type Cents } from '@bill/core';

interface Props {
  value: Cents | undefined;
  onChange(value: Cents | undefined): void;
  placeholder?: string;
  'aria-label': string;
  invalid?: boolean;
}

const toText = (value: Cents | undefined): string =>
  value === undefined ? '' : (value / 100).toFixed(2);

/**
 * Text input that holds a draft string while typing and commits integer cents
 * on blur. Typing "12." must not snap to "12.00" mid-keystroke, and "12,5"
 * must be accepted — the receipts are as often European as not.
 */
export function MoneyField({ value, onChange, placeholder, invalid, ...rest }: Props): React.ReactElement {
  const [text, setText] = useState(toText(value));

  useEffect(() => {
    setText(toText(value));
  }, [value]);

  const commit = (): void => {
    if (text.trim() === '') {
      onChange(undefined);
      return;
    }
    const parsed = parseMoney(text);
    if (parsed) {
      onChange(parsed.value);
      setText(toText(parsed.value));
    } else {
      setText(toText(value));
    }
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      className={`money${invalid ? ' invalid' : ''}`}
      value={text}
      placeholder={placeholder ?? '0.00'}
      aria-label={rest['aria-label']}
      aria-invalid={invalid || undefined}
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
      }}
    />
  );
}
